"use client";

// Per-book "Save offline" affordance for the library card. Pre-fetches the
// book detail, all chunks, and chapters into the IndexedDB offline cache so
// the reader can open the book without a connection (see ReaderOfflineGate).
// Mirrors the desktop app's "Save offline" / "Update offline copy" action;
// narration audio download is a follow-up.

import { useEffect, useMemo, useState } from "react";
import { Check, Download, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { createLumiApiClient } from "@/lib/lumi-api";
import { getChapters } from "./reader/api";
import {
  hasOfflineBook,
  isOfflineCacheSupported,
  writeOfflineBook,
} from "@/lib/offline/book-cache";

interface SaveOfflineButtonProps {
  bookId: string;
  /** Lets the caller size the control (the library card uses a 44px target). */
  className?: string;
}

type SaveState = "unknown" | "idle" | "saving" | "saved" | "error";

export default function SaveOfflineButton({
  bookId,
  className,
}: SaveOfflineButtonProps) {
  const api = useMemo(() => createLumiApiClient(), []);
  const [state, setState] = useState<SaveState>("unknown");

  useEffect(() => {
    let cancelled = false;
    hasOfflineBook(bookId)
      .then((saved) => {
        if (!cancelled) setState(saved ? "saved" : "idle");
      })
      .catch(() => {
        if (!cancelled) setState("idle");
      });
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  if (!isOfflineCacheSupported() || state === "unknown") return null;

  async function save() {
    setState("saving");
    try {
      const [book, chunks, chapters] = await Promise.all([
        api.getBook(bookId),
        api.listBookChunks(bookId),
        // Chapters power the offline table of contents; a book may have none.
        getChapters(bookId).catch(() => []),
      ]);
      await writeOfflineBook({
        bookId,
        book,
        chunks: chunks.items,
        chapters,
      });
      setState("saved");
    } catch {
      setState("error");
    }
  }

  const label =
    state === "saved"
      ? "Update offline copy"
      : state === "error"
        ? "Could not save offline — retry"
        : "Save offline";

  return (
    <Button
      aria-label={label}
      className={className}
      disabled={state === "saving"}
      onClick={save}
      size="icon"
      title={label}
      variant="outline"
    >
      {state === "saving" ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : state === "saved" ? (
        <Check className="h-4 w-4 text-app-body" />
      ) : (
        <Download
          className={`h-4 w-4 ${state === "error" ? "text-destructive" : ""}`}
        />
      )}
    </Button>
  );
}
